import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, PageHeader } from '@/components/ui/primitives';
import { EngineDashboard } from '@/components/EngineDashboard';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { CreateDialog } from '@/components/CreateDialog';
import {
  createSeoKeyword,
  fetchSeoKeywords,
  fetchSeoProjectOptions,
  runSeoRankCheck,
  type SeoKeywordRow,
} from '@/data/sources';
import { ApiError } from '@/lib/api';
import { generateFromKeyword, syncThenRetryGenerate, type GenerateOutcome } from '@/data/content-generate';
import { useAuth } from '@/auth/AuthContext';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/keywords — tracked keywords per project with the latest rank
 * observation (api/seo.yaml: GET keywords + GET rankings). Evidence-first
 * honesty: the keyword contract carries NO search-volume/CPC metrics, so those
 * columns are not rendered at all; positions come only from stored, source-
 * attributed observations, and a keyword that has never been checked shows an
 * explicit "—". Rank checks are paid + budget-guarded server-side: 503
 * provider_unavailable and 402 budget_exceeded render as honest human
 * guidance, never raw errors and never invented data. */

/** UK default per contract (DataForSEO location id 2826). */
function locationLabel(code: number | null): string {
  if (code === null) return '—';
  return code === 2826 ? 'United Kingdom (2826)' : String(code);
}

interface InlinePanel {
  title: string;
  body: string;
  /** optional in-app pointer (e.g. Spend & Budget on budget_exceeded). */
  link?: { to: string; label: string };
  /** Fail-closed staleness sync-now affordance (title-decoupling spec). When
   *  present, the panel renders a "Sync site content now" button (targetId
   *  non-null) that syncs the connector index then re-fires the SAME generate
   *  ONCE; targetId null = no connected site (guidance text, no button). */
  sync?: { targetId: number | null };
}

export function SeoKeywordsPage({ title = 'Keywords' }: { title?: string } = {}) {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<InlinePanel | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  // The LAST generate request (keyword + closure), captured so "Sync now" can
  // re-fire it verbatim after the connector index sync.
  const lastGenerate = useRef<null | { kw: SeoKeywordRow; gen: () => Promise<GenerateOutcome> }>(null);

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  // Fall back to the first project when nothing is selected (or the selected
  // project disappeared after a refetch).
  const projectId = resolveActiveProjectId(active, projects);
  const project = projects.find((p) => p.id === projectId) ?? null;

  const keywordsQ = useQuery({
    queryKey: ['seo', 'keywords', projectId],
    queryFn: () => fetchSeoKeywords(projectId as string),
    enabled: projectId !== null,
  });

  const handleAdd = async (values: Record<string, string>) => {
    if (projectId === null) throw new Error('Select a project first.');
    const locationRaw = (values.location_code ?? '').trim();
    let location_code: number | undefined;
    if (locationRaw !== '') {
      location_code = Number(locationRaw);
      if (!Number.isInteger(location_code) || location_code <= 0) {
        throw new Error('Location code must be a whole number (leave blank for the UK, 2826).');
      }
    }
    const language = (values.language ?? '').trim();
    try {
      const row = await createSeoKeyword(projectId, {
        keyword: (values.keyword ?? '').trim(),
        location_code,
        language: language === '' ? undefined : language,
      });
      setDialogOpen(false);
      setPanel(null);
      setNotice(`Now tracking “${row.keyword}”.`);
      await queryClient.invalidateQueries({ queryKey: ['seo', 'keywords', projectId] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        throw new Error('This keyword is already tracked for this project and location.');
      }
      // Project B: the 400-keyword hard cap (spec D6/§6). A distinct 422
      // 'keyword_cap_reached' (409 stays the duplicate signal above) surfaces
      // the cap + current count as clear inline guidance, never a raw error.
      if (err instanceof ApiError && (err.status === 422 || err.code === 'keyword_cap_reached')) {
        const d = err.details as { cap?: number; tracked?: number } | undefined;
        const cap = typeof d?.cap === 'number' ? d.cap : undefined;
        const tracked = typeof d?.tracked === 'number' ? d.tracked : undefined;
        throw new Error(
          cap !== undefined
            ? `This project already tracks the maximum of ${cap} keywords${tracked !== undefined ? ` (currently ${tracked})` : ''}. Remove a keyword before adding another.`
            : err.message || 'This project has reached its keyword limit. Remove a keyword before adding another.',
        );
      }
      if (err instanceof ApiError && err.status === 400) {
        throw new Error(err.message || 'Please check the keyword and try again.');
      }
      throw err instanceof Error ? err : new Error('Could not add the keyword. Please try again.');
    }
  };

  const handleCheck = async (kw: SeoKeywordRow) => {
    if (projectId === null) return;
    setNotice(null);
    setPanel(null);
    setCheckingId(kw.id);
    try {
      const res = await runSeoRankCheck(projectId, kw.id);
      setNotice(
        res.position !== null
          ? `“${kw.keyword}” — position #${res.position} recorded (source: ${res.source}).`
          : `“${kw.keyword}” checked — not found within the checked depth (source: ${res.source}).`,
      );
      await queryClient.invalidateQueries({ queryKey: ['seo', 'keywords', projectId] });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 503 || err.code === 'provider_unavailable')) {
        setPanel({
          title: 'Rank checking isn’t available yet',
          body: 'Live rank checks need the data provider connection (DataForSEO), which isn’t configured for this environment yet. Nothing was charged and your tracked keywords are unaffected — previously stored observations still show below.',
        });
      } else if (err instanceof ApiError && (err.status === 402 || err.code === 'budget_exceeded')) {
        setPanel({
          title: 'Monthly provider budget reached',
          body: 'This check would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. Checks resume when the budget resets next month.',
          link: { to: '/seo/spend', label: 'Review spend & budget →' },
        });
      } else {
        setPanel({
          title: 'The rank check could not be completed',
          body:
            err instanceof ApiError && err.message
              ? `${err.message} No result was stored — try again.`
              : 'The data service returned an unexpected response. No result was stored — try again.',
        });
      }
    } finally {
      setCheckingId(null);
    }
  };

  // GAP #1 MODE (i): the row IS the selection — generate a blog draft targeting
  // this tracked keyword. Shared server-side dedup runs first, so a duplicate is
  // refused 409 and rendered as a clear "already exists / published on <site>"
  // notice (never a generic error). Draft only; nothing is published.
  const applyGenerateOutcome = (kw: SeoKeywordRow, outcome: GenerateOutcome, fromRetry = false) => {
    if (outcome.kind === 'ready') {
      const staleNote =
        outcome.inventoryStale && outcome.inventoryAsOf
          ? ` Angle varied against your site’s published content as of ${formatRelative(
              outcome.inventoryAsOf,
            )} — sync the index to refresh.`
          : '';
      setPanel(null);
      setNotice(
        `Blog draft ready for “${kw.keyword}”${outcome.title ? ` — “${outcome.title}”` : ''}. ` +
          'Find it in the Blog / Articles library — draft only, nothing published or sent.' +
          staleNote,
      );
    } else if (outcome.kind === 'duplicate') {
      setPanel({ title: 'That content already exists', body: outcome.message });
    } else if (outcome.kind === 'inventory_not_synced') {
      if (fromRetry) {
        // The sync ran but found zero published posts — honest dead-end, no loop.
        setPanel({
          title: 'No published content to vary against',
          body: 'The site was synced, but it has no published posts yet for the new article to vary against. Nothing was generated — publish some content first, then try again.',
        });
      } else {
        setPanel({
          title: 'Sync the site’s content first',
          body: outcome.message,
          sync: { targetId: outcome.syncTargetId },
        });
      }
    } else {
      setPanel({ title: 'Couldn’t generate for that keyword', body: outcome.message });
    }
  };

  // "Sync site content now" -> sync the connector index, then re-fire the SAME
  // generate ONCE. Sync-route errors (503/409/404) render honest inline states.
  const syncMut = useMutation({
    mutationFn: () => {
      const tid = panel?.sync?.targetId ?? null;
      const last = lastGenerate.current;
      if (tid == null || !last) throw new Error('no_sync_target');
      return syncThenRetryGenerate(tid, last.gen);
    },
    onSuccess: ({ outcome }) => {
      const last = lastGenerate.current;
      if (last) applyGenerateOutcome(last.kw, outcome, true);
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 503 || err.code === 'connector_disabled')) {
        setPanel({
          title: 'Site sync unavailable',
          body: 'The site connection isn’t active — check it under Connections, then try again. Nothing was generated.',
        });
      } else if (err instanceof ApiError && err.status === 409) {
        setPanel({
          title: 'Site sync isn’t available right now',
          body: `${err.message || 'The site’s content index can’t be synced at the moment.'} Try again shortly — nothing was generated.`,
        });
      } else if (err instanceof ApiError && err.status === 404) {
        setPanel({
          title: 'Site sync unavailable',
          body: 'That site connection couldn’t be found — check it under Connections. Nothing was generated.',
        });
      } else {
        setPanel({
          title: 'Site sync could not be completed',
          body:
            err instanceof ApiError && err.message
              ? `${err.message} Nothing was generated — try again.`
              : 'The sync service returned an unexpected response. Nothing was generated — try again.',
        });
      }
    },
  });

  const handleGenerate = async (kw: SeoKeywordRow) => {
    if (projectId === null) return;
    setNotice(null);
    setPanel(null);
    setGeneratingId(kw.id);
    const gen = () =>
      generateFromKeyword({
        id: kw.id,
        keyword: kw.keyword,
        location_code: kw.location_code,
        language: kw.language,
      });
    lastGenerate.current = { kw, gen };
    try {
      applyGenerateOutcome(kw, await gen());
    } catch (err) {
      setPanel({
        title: 'The blog draft could not be generated',
        body:
          err instanceof ApiError && err.message
            ? `${err.message} Nothing was created — try again.`
            : 'The content service returned an unexpected response. Nothing was created — try again.',
      });
    } finally {
      setGeneratingId(null);
    }
  };

  const addDialog = canManage ? (
    <CreateDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title="Track keyword"
      description={
        project
          ? `Add a keyword to ${project.label}. Rankings are checked on demand — every check is source-attributed and budget-guarded.`
          : undefined
      }
      submitLabel="Track keyword"
      onSubmit={handleAdd}
      fields={[
        {
          name: 'keyword',
          label: 'Keyword',
          required: true,
          placeholder: 'e.g. mobile pilates cardiff',
          hint: 'The search phrase to track, exactly as people would type it.',
        },
        {
          name: 'location_code',
          label: 'Location code',
          placeholder: '2826',
          hint: 'DataForSEO location id. Leave blank for United Kingdom (2826).',
        },
        {
          name: 'language',
          label: 'Language',
          placeholder: 'en',
          hint: 'Language code. Leave blank for English (en).',
        },
      ]}
    />
  ) : null;

  const addButton =
    canManage && projects.length > 0 ? (
      <Button variant="primary" onClick={() => setDialogOpen(true)}>
        ＋ Track keyword
      </Button>
    ) : null;

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }

  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} />
        <EmptyState
          title="No domains to track keywords for"
          teach="Keywords live inside an SEO project. Add your first domain on the Projects page, then come back here to choose the search terms you want to rank for."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const keywords = keywordsQ.data ?? [];

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => {
          void select(Number(e.target.value));
          setNotice(null);
          setPanel(null);
        }}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        actions={
          <>
            {projectSelector}
            {addButton}
          </>
        }
      />

      {notice && (
        <div className="rounded-md border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
          {notice}
        </div>
      )}

      {panel && (
        <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
          <div className="text-sm font-medium text-text-primary">{panel.title}</div>
          <p className="mt-1 text-sm text-text-secondary">{panel.body}</p>
          {panel.link && (
            <Link
              to={panel.link.to}
              className="mt-2 inline-block text-sm font-medium text-engine hover:underline"
            >
              {panel.link.label}
            </Link>
          )}
          {panel.sync &&
            (panel.sync.targetId != null ? (
              <div className="mt-3">
                <Button
                  variant="primary"
                  disabled={syncMut.isPending}
                  onClick={() => syncMut.mutate()}
                >
                  {syncMut.isPending ? 'Syncing…' : 'Sync site content now'}
                </Button>
              </div>
            ) : (
              <p className="mt-2 text-sm text-text-tertiary">
                No connected site found for this project — connect it under Connections, then sync its
                content index.
              </p>
            ))}
        </div>
      )}

      <EngineDashboard
        engineName="SEO Engine"
        heroLabel={project ? `Tracked keywords — ${project.label}` : 'Tracked keywords'}
        hero={<span data-tabular>{keywordsQ.isSuccess ? keywords.length : '—'}</span>}
        heroSub="Positions come from stored DataForSEO SERP observations — always source-attributed. Search volume and CPC aren’t provided by the API yet, so they aren’t shown."
      >
        {keywordsQ.isPending ? (
          <LoadingState rows={4} />
        ) : keywordsQ.error ? (
          <ErrorState error={keywordsQ.error} onRetry={() => void keywordsQ.refetch()} />
        ) : keywords.length === 0 ? (
          <EmptyState
            title="No keywords tracked yet"
            teach={`Track the search phrases that matter for ${project?.domain ?? 'this domain'} — each rank check is run on demand, source-attributed, and charged against your monthly provider budget.`}
            action={
              canManage ? (
                <Button variant="primary" onClick={() => setDialogOpen(true)}>
                  ＋ Track keyword
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                    <th className="px-3 py-2 font-semibold">Keyword</th>
                    <th className="px-3 py-2 font-semibold">Location</th>
                    <th className="px-3 py-2 font-semibold">Language</th>
                    <th className="px-3 py-2 font-semibold">Latest position</th>
                    <th className="px-3 py-2 font-semibold">Last checked</th>
                    <th className="px-3 py-2 font-semibold">Source</th>
                    {canManage && (
                      <th className="px-3 py-2 text-right font-semibold">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((kw) => (
                    <tr key={kw.id} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                      <td className="px-3 py-2.5 text-sm font-medium text-text-primary">{kw.keyword}</td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary">{locationLabel(kw.location_code)}</td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary">{kw.language ?? '—'}</td>
                      <td className="px-3 py-2.5">
                        {kw.latest_rank === null ? (
                          <span className="text-sm text-text-tertiary" title="No rank checks have run for this keyword yet">
                            —
                          </span>
                        ) : kw.latest_rank.position === null ? (
                          <span className="text-sm text-text-tertiary" title="Checked, but not found within the checked depth">
                            Not found
                          </span>
                        ) : (
                          <span className="font-mono text-sm font-semibold text-text-primary" data-tabular>
                            #{kw.latest_rank.position}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-text-secondary">
                        {kw.latest_rank ? formatRelative(kw.latest_rank.checked_at) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-text-tertiary">
                        {kw.latest_rank?.source ?? '—'}
                      </td>
                      {canManage && (
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              disabled={generatingId !== null || syncMut.isPending}
                              onClick={() => void handleGenerate(kw)}
                              title="Generate a blog draft targeting this tracked keyword — draft only, checked for duplicates first, nothing published"
                            >
                              {generatingId === kw.id ? 'Generating…' : 'Generate blog'}
                            </Button>
                            <Button
                              variant="ghost"
                              disabled={checkingId !== null}
                              onClick={() => void handleCheck(kw)}
                              title="Runs one live SERP check now — pre-priced and charged against the monthly provider budget"
                            >
                              {checkingId === kw.id ? 'Checking…' : 'Check rank'}
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </EngineDashboard>

      {addDialog}
    </div>
  );
}
